import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  WorkspaceTableSkeleton,
  type WorkspaceTableSkeletonColumn,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * Loading shapes for Plans and Billing (`/home/account-settings?section=plans`
 * and `?section=billing`). Each mirrors the real block it stands in for, so the
 * section appears once instead of a centred spinner and then the content.
 */

/** Mirrors a `WenextPlansCatalog` plan card: name, blurb, price, features, CTA. */
const PlanCardSkeleton: React.FC = () => (
  <div className="flex flex-col rounded-2xl border border-[#EAECF0] bg-white p-5 sm:p-6">
    <Skeleton className="mb-4 h-5 w-24 rounded" />
    <div className="mb-5 min-h-[40px] space-y-1.5">
      <Skeleton className="h-3.5 w-full rounded" />
      <Skeleton className="h-3.5 w-3/4 rounded" />
    </div>
    <Skeleton className="mb-5 h-8 w-32 rounded" />
    <div className="mb-6 flex-1 space-y-2.5">
      {[70, 85, 60, 78, 66].map((w, i) => (
        <div key={i} className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
          <Skeleton className="h-3.5 rounded" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
    <Skeleton className="h-10 w-full rounded-lg" />
  </div>
);

export const PlanCardsSkeleton: React.FC = () => (
  <div
    className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
    aria-busy="true"
    data-testid="plan-cards-skeleton"
  >
    {[0, 1, 2].map((i) => (
      <PlanCardSkeleton key={i} />
    ))}
  </div>
);

/** Mirrors the Overview's current-plan card (`BillingPlanHero` in a section card). */
export const BillingPlanHeroSkeleton: React.FC = () => (
  <div
    className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white"
    aria-busy="true"
    data-testid="billing-plan-hero-skeleton"
  >
    <div className="p-5 sm:p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-6 w-40 rounded" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-3.5 w-full max-w-xl rounded" />
          <Skeleton className="h-3.5 w-2/3 max-w-md rounded" />
        </div>
        <Skeleton className="h-10 w-[140px] shrink-0 rounded-lg" />
      </div>
      <div className="mt-5 flex flex-col gap-4 border-t border-[#F2F4F7] pt-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-20 rounded" />
          <Skeleton className="h-7 w-28 rounded" />
        </div>
        <Skeleton className="h-3.5 w-48 rounded" />
      </div>
    </div>
  </div>
);

/** Mirrors the add-on catalog grid. */
export const AddonCardsSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" data-testid="addon-cards-skeleton">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex h-full flex-col rounded-2xl border border-[#EAECF0] bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-4 w-28 rounded" />
          <Skeleton className="h-5 w-14 rounded-md" />
        </div>
        <div className="mt-1.5 min-h-[2.5em] space-y-1.5">
          <Skeleton className="h-3 w-full rounded" />
          <Skeleton className="h-3 w-2/3 rounded" />
        </div>
        <Skeleton className="mt-4 h-9 w-full rounded-lg" />
      </div>
    ))}
  </div>
);

// Invoice number, date, plan, amount, status, actions.
const INVOICE_COLUMNS: WorkspaceTableSkeletonColumn[] = [
  { width: 'w-24' },
  { width: 'w-20' },
  { width: 'w-28', cellClassName: 'hidden sm:table-cell' },
  { width: 'w-16' },
  { width: 'w-16' },
  { width: 'w-8', actions: true },
];

/** The Invoices tab before the page has loaded: toolbar and the table card. */
export const InvoicesTabSkeleton: React.FC = () => (
  <div aria-busy="true" data-testid="invoices-tab-skeleton">
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      <Skeleton className="h-9 min-w-0 flex-1 rounded-lg sm:w-[260px] sm:max-w-[280px] sm:flex-none" />
      <Skeleton className="hidden h-9 w-[92px] rounded-lg sm:block" />
    </div>
    <WorkspaceTableSkeleton columns={INVOICE_COLUMNS} rows={6} />
  </div>
);
